import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Button,
  CircularProgress, Alert, MenuItem, TextField, Checkbox, FormControlLabel,
} from '@mui/material';
import { Grading } from '@mui/icons-material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import '@fontsource/kristi';   // fonte da assinatura (a mesma da Comunicação e do Planner Férias)
import ModuleHeader from './components/ModuleHeader';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_URL } from './config';
import BotaoArquivar from './BotaoArquivar';

// Bloco 2 (14/10/2026): a tela imita o modelo de papel "QUADRO DE ACOMPANHAMENTO DO SUPERVISOR".
// A tela não faz conta: só mostra o que o motor (/api/quadro-supervisor/) responde.
// Positivos em vermelho #c00000, o mesmo do Fechamento de Ciclo.

const VERMELHO = '#c00000';
const FUSO = 'America/Bahia';
const CHAVE_COPIADOS = 'tovis-quadro-sup-copiados';
// Bloco 4 (14/10/2026): logo e assinatura reaproveitados da Comunicação / Planner Férias
const URL_LOGO = `${API_URL}/api/comunicacao/logo`;            // mesmo logo do Planner Férias e da Comunicação
const AZUL_CANETA = '#1A3A8F';                                 // mesma cor da assinatura da Comunicação/Planner
const ASSINATURA_PADRAO = 'Walber Reis dos Santos';            // igual ao Planner Férias (ASSINATURA_PADRAO)
const CARGO_ASSINATURA = 'Supervisor dos Agentes de Combate às Endemias';   // igual ao Planner Férias
const CHAVE_ASSIN_ELETRONICA = 'tovis-quadro-sup-assinatura';
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho',
  'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

interface Ciclo { id: number; nome: string; ativo: boolean; }
interface Area { id: number; nome: string; }
interface Bloco {
  total_registros: number; agentes: number; dias_trabalhados: number;
  imoveis_trabalhados: number; imoveis_informados: number; imoveis_tratados: number;
  inspecionados: number; recuperados: number; imoveis_pendentes: number;
  amostras_coletadas: number; amostras_analisadas: number;
  amostras_coletadas_rotina?: number; amostras_coletadas_liraa?: number;
  larvicida_gramas: number; depositos_tratados: number; agentes_dia: number;
  imoveis_positivos: number; dep_positivos: number;
  iip: number | null; ib: number | null;
  pe_trabalhados?: number; inseticida_gasto?: number | null; inseticida_unidade?: string;
}
interface Liraa {
  ciclos: string[]; tubitos_examinados: number; tubitos_coletados?: number;
  imoveis_positivos_aegypti: number; imoveis_positivos_albopictus: number;
  recipientes_positivos_aegypti: number; recipientes_positivos_albopictus: number;
}
interface CicloPeriodo {
  id: number; nome: string; data_inicio: string | null; data_fim: string | null;
  primeiro_boletim: string; ultimo_boletim: string;
}
interface Quadro {
  periodo: { inicio: string; fim: string };
  ciclo_nome: string | null;
  sede: Bloco; zona_rural: Bloco; pe: Bloco; total_geral: Bloco;
  liraa: Liraa;
  ciclos_periodo: CicloPeriodo[];
}

interface Props { onVoltar: () => void; }

// ── Datas no horário local (America/Bahia), nunca em UTC ──
export function hojeLocal(agora: Date = new Date()): { ano: number; mes: number; dia: number } {
  const p = new Intl.DateTimeFormat('en-CA', { timeZone: FUSO, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(agora);
  const v = (t: string) => Number(p.find(x => x.type === t)?.value);
  return { ano: v('year'), mes: v('month'), dia: v('day') };
}
const dois = (n: number) => String(n).padStart(2, '0');
export function limitesDoMes(ano: number, mes: number): { inicio: string; fim: string } {
  const ultimo = new Date(Date.UTC(ano, mes, 0)).getUTCDate();   // mes 1–12: dia 0 do mês seguinte
  return { inicio: `${ano}-${dois(mes)}-01`, fim: `${ano}-${dois(mes)}-${dois(ultimo)}` };
}
export function mesAtualLocal(agora: Date = new Date()) {
  const h = hojeLocal(agora);
  return limitesDoMes(h.ano, h.mes);
}

// ── Formatação (zero = "0"; "—" só quando o dado não existe) ──
export function fmtDataBR(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a}`;
}
export function fmtNum(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}
export function fmtGramas(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' g';
}
export function fmtIndice(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
export function fmtInseticida(v: number | null | undefined, unidade?: string): string {
  if (v === null || v === undefined) return '—';
  return `${fmtNum(v)}${unidade ? ' ' + unidade : ''}`;
}
export function nomeDoMes(inicio: string, fim: string): string {
  const [a1, m1] = inicio.split('-').map(Number);
  const [a2, m2] = fim.split('-').map(Number);
  const n1 = `${MESES[m1 - 1]}/${a1}`;
  return (a1 === a2 && m1 === m2) ? n1 : `${n1} a ${MESES[m2 - 1]}/${a2}`;
}

/** Início e fim de cada ciclo NO PERÍODO: datas do cadastro, presas ao período.
 *  Sem data final (ou terminando depois do período) → último boletim do período + "(em andamento)". */
export function ciclosDaFolha(ciclos: CicloPeriodo[], periodo: { inicio: string; fim: string }) {
  return ciclos.map(c => {
    const ini = c.data_inicio && c.data_inicio > periodo.inicio ? c.data_inicio : periodo.inicio;
    const terminou = !!c.data_fim && c.data_fim <= periodo.fim;
    return {
      nome: c.nome,
      inicio: fmtDataBR(ini),
      fim: terminou ? fmtDataBR(c.data_fim) : fmtDataBR(c.ultimo_boletim),
      emAndamento: !terminou,
    };
  });
}

/** Conta aberta de "Amostras colet." (Bloco 3): rotina + LIRAa = total. */
export function contaAmostras(b: Bloco): string {
  return `rotina ${fmtNum(b.amostras_coletadas_rotina)} + LIRAa ${fmtNum(b.amostras_coletadas_liraa)} = ${fmtNum(b.amostras_coletadas)}`;
}

/** Nome na assinatura: igual ao Planner Férias — usuário com uma palavra só ("Supervisor") vira o nome completo. */
export function nomeNaAssinatura(nomeUsuario?: string | null): string {
  const n = (nomeUsuario || '').trim();
  return n.split(/\s+/).length >= 2 ? n : ASSINATURA_PADRAO;
}

/** Regras do papel (impressão, PDF e cópia do Arquivar): preto, positivos em vermelho, assinatura azul, sem marcas. */
function cssPapel(f: string): string {
  return `
    ${f} .no-print { display: none !important; }
    ${f} { border: none !important; padding: 0 !important; background: white !important; border-radius: 0 !important; }
    ${f}, ${f} * { color: #000 !important; }
    ${f} .qs-pos { color: ${VERMELHO} !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    ${f} .qs-assin-nome { color: ${AZUL_CANETA} !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    ${f} .qs-campo { background: transparent !important; opacity: 1 !important; cursor: default; padding: 2px 6px !important; gap: 8px !important; flex: 1 1 auto !important; }
    ${f} .qs-campo > span:first-child { font-size: 0.8rem !important; }
    ${f} .qs-secao { break-inside: avoid; margin-top: 10px !important; }
    ${f} .qs-logo { height: 16mm !important; }
    ${f} .qs-assin { margin-top: 26px !important; break-inside: avoid; }
  `;
}

export interface CampoFolha { id: string; rotulo: string; valor: string; positivo?: boolean; }
export interface SecaoFolha { titulo: string; linhas: CampoFolha[][]; }

/** A folha, na ordem exata do papel. Usada pela tela e pelo teste. */
export function montarFolha(q: Quadro): SecaoFolha[] {
  const s = q.sede, r = q.zona_rural, p = q.pe;
  return [
    { titulo: 'SEDE', linhas: [
      [{ id: 'sede.trab', rotulo: 'Imóveis trab.', valor: fmtNum(s.imoveis_trabalhados) },
       { id: 'sede.trat', rotulo: 'Imóv. tratados', valor: fmtNum(s.imoveis_tratados) },
       { id: 'sede.insp', rotulo: 'Imóv. inspecionados', valor: fmtNum(s.inspecionados) }],
      [{ id: 'sede.amc', rotulo: 'Amostras colet.', valor: fmtNum(s.amostras_coletadas) },
       { id: 'sede.pend', rotulo: 'Imóv. pendentes', valor: fmtNum(s.imoveis_pendentes) },
       { id: 'sede.recup', rotulo: 'Imóv. Recuperados', valor: fmtNum(s.recuperados) }],
      [{ id: 'sede.larv', rotulo: 'Larvicida gasto', valor: fmtGramas(s.larvicida_gramas) },
       { id: 'sede.dtrat', rotulo: 'Depósitos trat.', valor: fmtNum(s.depositos_tratados) }],
      [{ id: 'sede.hd', rotulo: 'Agentes/dia/trab', valor: fmtNum(s.agentes_dia) },
       { id: 'sede.dpos', rotulo: 'Depósitos positivos', valor: fmtNum(s.dep_positivos), positivo: true },
       { id: 'sede.ipos', rotulo: 'Imóv. Positivos', valor: fmtNum(s.imoveis_positivos), positivo: true }],
    ] },
    { titulo: 'ZONA RURAL', linhas: [
      [{ id: 'rural.trab', rotulo: 'Imóv. Trab.', valor: fmtNum(r.imoveis_trabalhados) },
       { id: 'rural.insp', rotulo: 'Imóv. Inspec.', valor: fmtNum(r.inspecionados) },
       { id: 'rural.larv', rotulo: 'Larvicida gasto', valor: fmtGramas(r.larvicida_gramas) }],
      [{ id: 'rural.amc', rotulo: 'Amostras colet.', valor: fmtNum(r.amostras_coletadas) },
       { id: 'rural.ipos', rotulo: 'Imóv. Positivos.', valor: fmtNum(r.imoveis_positivos), positivo: true },
       { id: 'rural.dpos', rotulo: 'Depósitos positivos.', valor: fmtNum(r.dep_positivos), positivo: true }],
    ] },
    { titulo: 'PONTOS ESTRATÉGICOS', linhas: [
      [{ id: 'pe.trab', rotulo: 'P.E trab.', valor: fmtNum(p.pe_trabalhados) },
       { id: 'pe.larv', rotulo: 'Larvicida gasto', valor: fmtGramas(p.larvicida_gramas) },
       { id: 'pe.inset', rotulo: 'insetic. Gasto', valor: fmtInseticida(p.inseticida_gasto, p.inseticida_unidade) }],
    ] },
  ];
}

function getUrlParams() {
  const p = new URLSearchParams(window.location.search);
  const mes = mesAtualLocal();
  return {
    inicio: p.get('inicio') || mes.inicio,
    fim:    p.get('fim')    || mes.fim,
    ciclo:  p.get('ciclo')  || '',
    area:   p.get('area')   || '',
    se:     p.get('se')     || '',
  };
}

// Marcas de "copiado": sessionStorage, por mês + área; guarda o valor copiado (se o número mudar, a marca some)
type Copiados = Record<string, string>;
function lerCopiados(chave: string): Copiados {
  try {
    const tudo = JSON.parse(window.sessionStorage.getItem(CHAVE_COPIADOS) || '{}');
    const c = tudo && typeof tudo === 'object' ? tudo[chave] : null;
    return c && typeof c === 'object' ? c : {};
  } catch { return {}; }
}
function gravarCopiados(chave: string, c: Copiados) {
  try {
    const tudo = JSON.parse(window.sessionStorage.getItem(CHAVE_COPIADOS) || '{}') || {};
    tudo[chave] = c;
    window.sessionStorage.setItem(CHAVE_COPIADOS, JSON.stringify(tudo));
  } catch { /* sem armazenamento: as marcas valem só enquanto a tela estiver aberta */ }
}

function CampoPapel({ campo, copiado, onClick }: { campo: CampoFolha; copiado: boolean; onClick: () => void }) {
  return (
    <Box
      component="button" type="button" onClick={onClick}
      className={`qs-campo${copiado ? ' qs-copiado' : ''}`}
      title={copiado ? 'Copiado — clique para desmarcar' : 'Clique quando copiar para o papel'}
      sx={{
        display: 'flex', alignItems: 'baseline', gap: 1.5, flex: '1 1 0', minWidth: 0,
        px: 1.5, py: 1, border: 'none', borderRadius: 1, cursor: 'pointer', textAlign: 'left',
        font: 'inherit', color: 'text.primary',
        bgcolor: copiado ? 'action.selected' : 'transparent',
        opacity: copiado ? 0.55 : 1,
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
      }}
    >
      <Typography component="span" sx={{ fontSize: '0.95rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {campo.rotulo}
      </Typography>
      <Typography component="span" className={campo.positivo ? 'qs-pos' : undefined} sx={{
        fontSize: '1.6rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
        borderBottom: '1px solid', borderColor: 'text.primary', px: 1, minWidth: 64, textAlign: 'center',
        color: campo.positivo ? VERMELHO : 'text.primary',
      }}>
        {campo.valor}
      </Typography>
      {copiado && (
        <Typography component="span" className="no-print" sx={{ fontSize: '0.9rem', color: 'success.main', fontWeight: 700 }}>✓</Typography>
      )}
    </Box>
  );
}

function ItemFaixa({ rotulo, valor, vermelho = false }: { rotulo: string; valor: string; vermelho?: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.3 }}>
      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>{rotulo}</Typography>
      <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: vermelho ? VERMELHO : 'text.primary' }}>
        {valor}
      </Typography>
    </Box>
  );
}

function GrupoFaixa({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <Box sx={{ flex: '1 1 220px', minWidth: 200, p: 1.5, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>{titulo}</Typography>
      {children}
    </Box>
  );
}

export default function QuadroSupervisor({ onVoltar }: Props) {
  const { token, usuario } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const printRef = useRef<HTMLDivElement>(null);

  const params = getUrlParams();
  const [inicio, setInicio] = useState(params.inicio);
  const [fim, setFim]       = useState(params.fim);
  const [cicloId, setCicloId] = useState(params.ciclo);
  const [ciclos, setCiclos]   = useState<Ciclo[]>([]);
  const [areas, setAreas]     = useState<Area[]>([]);
  const [filtroArea, setFiltroArea] = useState(params.area);
  const [filtroSE, setFiltroSE]     = useState(params.se);
  const [quadro, setQuadro]   = useState<Quadro | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  // Bloco 8: filtros que geraram o quadro que está NA TELA (o nome do arquivo vem daqui, não das caixas)
  const [carregado, setCarregado] = useState<{ ciclo: string; area: string; se: string } | null>(null);
  const [copiados, setCopiados] = useState<Copiados>({});

  const q = quadro;
  const chaveMarcas = q ? `${q.periodo.inicio}_${q.periodo.fim}|a${carregado?.area || 'todas'}` : '';
  useEffect(() => { if (chaveMarcas) setCopiados(lerCopiados(chaveMarcas)); }, [chaveMarcas]);

  const folha = useMemo(() => (q ? montarFolha(q) : []), [q]);

  const alternar = (c: CampoFolha) => {
    if (!chaveMarcas) return;
    const novo = { ...copiados };
    if (novo[c.id] === c.valor) delete novo[c.id]; else novo[c.id] = c.valor;
    setCopiados(novo); gravarCopiados(chaveMarcas, novo);
  };
  // Assinatura eletrônica (marcada por padrão; preferência guardada na sessão)
  const [eletronica, setEletronica] = useState<boolean>(() => {
    try { return window.sessionStorage.getItem(CHAVE_ASSIN_ELETRONICA) !== '0'; } catch { return true; }
  });
  const mudarEletronica = (v: boolean) => {
    setEletronica(v);
    try { window.sessionStorage.setItem(CHAVE_ASSIN_ELETRONICA, v ? '1' : '0'); } catch { /* sem armazenamento */ }
  };
  const [semLogo, setSemLogo] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const assinatura = nomeNaAssinatura((usuario as any)?.nome);

  // Antes de imprimir/baixar: espera o logo e a fonte da assinatura (limite de 3 s, como no Planner Férias)
  const esperarRecursos = async () => {
    const limite = new Promise<void>(res => setTimeout(res, 3000));
    const fonte = eletronica && (document as any).fonts
      ? Promise.all([(document as any).fonts.load("30px 'Kristi'"), (document as any).fonts.ready]).then(() => undefined, () => undefined)
      : Promise.resolve();
    const img = printRef.current?.querySelector('img.qs-logo') as HTMLImageElement | null;
    const logo = img && !img.complete
      ? new Promise<void>(res => { img.addEventListener('load', () => res(), { once: true }); img.addEventListener('error', () => res(), { once: true }); })
      : Promise.resolve();
    await Promise.race([Promise.all([fonte, logo]), limite]);
  };
  const imprimir = async () => { await esperarRecursos(); window.print(); };
  const baixarPdf = async () => {
    const el = printRef.current;
    if (!el || !quadro) return;
    setGerandoPdf(true);
    try {
      await esperarRecursos();
      const html2pdf = (await import('html2pdf.js')).default;   // mesmo motor de PDF do FAD-07
      el.classList.add('qs-pdf');
      await html2pdf().set({
        margin: [15, 15, 15, 15],
        filename: `Quadro_Supervisor_${quadro.periodo.inicio.slice(0, 7)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      }).from(el).save();
    } catch (e) {
      setErro('Não foi possível gerar o PDF. Use "Imprimir" e salve como PDF pelo navegador.');
    } finally {
      el.classList.remove('qs-pdf');
      setGerandoPdf(false);
    }
  };

  const desmarcarTudo = () => { setCopiados({}); if (chaveMarcas) gravarCopiados(chaveMarcas, {}); };
  const nCampos = folha.reduce((n, s) => n + s.linhas.reduce((m, l) => m + l.length, 0), 0);
  const nCopiados = folha.reduce((n, s) => n + s.linhas.reduce((m, l) => m + l.filter(c => copiados[c.id] === c.valor).length, 0), 0);

  // Arquivos: dados do documento para o botão "Arquivar" (arquiva a folha)
  const pedidoArquivo = useMemo(() => {
    if (!quadro || !carregado || !carregado.ciclo) return null;
    const { ciclo, area, se } = carregado;
    const nomeCiclo = ciclos.find(c => String(c.id) === String(ciclo))?.nome ?? quadro.ciclo_nome ?? `Ciclo ${ciclo}`;
    const nomeArea = area ? (areas.find(a => String(a.id) === String(area))?.nome ?? String(area)) : '';
    return {
      pasta: 'quadro_supervisor' as const, tipo: 'quadro_supervisor',
      chave: `quadro_supervisor|c${ciclo}|a${area || 'todas'}|se${se || 'todas'}`,
      titulo: `Quadro do Supervisor · ${nomeCiclo}${nomeArea ? ` · ${nomeArea}` : ''}${se ? ` · SE ${se}` : ''}`,
      ciclo_id: Number(ciclo), area_id: area && /^\d+$/.test(String(area)) ? Number(area) : null,
      se: se ? Number(se) : null, referencia: nomeCiclo, avisos: [] as string[],
    };
  }, [quadro, carregado, ciclos, areas]);

  useEffect(() => { document.title = 'Quadro do Supervisor · Tovis'; return () => { document.title = 'Tovis'; }; }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/ciclos/`, { headers })
      .then(r => setCiclos(r.data))
      .catch(() => {});
    axios.get(`${API_URL}/api/areas/`, { headers })
      .then(r => setAreas(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buscar = useCallback(async (ini: string, f: string, cid: string, area: string, se: string) => {
    if (!ini || !f) return;
    setCarregando(true); setErro('');
    try {
      const p: any = { data_inicio: ini, data_fim: f };
      if (cid)  p.ciclo_id = cid;
      if (area) p.area_id  = area;
      if (se)   p.se       = se;
      const res = await axios.get(`${API_URL}/api/quadro-supervisor/`, { headers, params: p });
      setQuadro(res.data);
      setCarregado({ ciclo: cid, area, se });
      const sp = new URLSearchParams({ inicio: ini, fim: f });
      if (cid)  sp.set('ciclo', cid);
      if (area) sp.set('area',  area);
      if (se)   sp.set('se',    se);
      window.history.replaceState(null, '', `/quadro-supervisor?${sp.toString()}`);
    } catch (e: any) {
      setErro(e.response?.data?.detail || 'Erro ao carregar dados.');
    } finally { setCarregando(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { buscar(inicio, fim, cicloId, filtroArea, filtroSE); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const aplicarFiltro = () => buscar(inicio, fim, cicloId, filtroArea, filtroSE);
  const escolherMes = (v: string) => {
    const [a, m] = v.split('-').map(Number);
    if (!a || !m) return;
    const l = limitesDoMes(a, m);
    setInicio(l.inicio); setFim(l.fim);
  };

  const ciclosFolha = q ? ciclosDaFolha(q.ciclos_periodo || [], q.periodo) : [];
  const nomeArea = carregado?.area ? areas.find(a => String(a.id) === String(carregado.area))?.nome : '';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Box className="no-print">
        <ModuleHeader
          titulo="Quadro do Supervisor"
          subtitulo="Quadro de acompanhamento do supervisor"
          icone={<DashboardIcon sx={{ fontSize: 32 }} />}
          accentColor="#1B5E20"
          onVoltar={onVoltar}
        />
      </Box>

      {/* ── Filtros (fora da impressão) ── */}
      <Box className="no-print filtros-bar" sx={{
        bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider',
        px: { xs: 2, md: 4 }, py: 1.5,
        display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end',
      }}>
        <TextField label="Mês" type="month" size="small" value={inicio.slice(0, 7)}
          onChange={e => escolherMes(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 170 }} />
        <TextField label="Início" type="date" size="small" value={inicio}
          onChange={e => setInicio(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 155 }} />
        <TextField label="Fim" type="date" size="small" value={fim}
          onChange={e => setFim(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 155 }} />
        <TextField select label="Ciclo" size="small" value={cicloId}
          onChange={e => setCicloId(e.target.value)} sx={{ width: 200 }}>
          <MenuItem value="">Todos os ciclos</MenuItem>
          {ciclos.map(c => (
            <MenuItem key={c.id} value={String(c.id)}>{c.nome}{c.ativo ? ' ●' : ''}</MenuItem>
          ))}
        </TextField>
        <TextField select label="Área" size="small" value={filtroArea}
          onChange={e => setFiltroArea(e.target.value)} sx={{ width: 180 }}>
          <MenuItem value="">Todas</MenuItem>
          {areas.map(a => (
            <MenuItem key={a.id} value={String(a.id)}>{a.nome}</MenuItem>
          ))}
        </TextField>
        <TextField select label="SE" size="small" value={filtroSE}
          onChange={e => setFiltroSE(e.target.value)} sx={{ width: 110 }}>
          <MenuItem value="">Todas</MenuItem>
          {Array.from({ length: 52 }, (_, i) => i + 1).map(n => (
            <MenuItem key={n} value={String(n)}>SE {n}</MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={aplicarFiltro} sx={{ fontWeight: 700, borderRadius: 2, height: 40 }}>
          Filtrar
        </Button>
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={imprimir} sx={{ height: 40, borderRadius: 2 }}>
          Imprimir
        </Button>
        <Button variant="outlined" startIcon={gerandoPdf ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
          onClick={baixarPdf} disabled={!q || gerandoPdf} sx={{ height: 40, borderRadius: 2 }}>
          Baixar PDF
        </Button>
        <BotaoArquivar alvo={() => printRef.current} pedido={pedidoArquivo} />
        <FormControlLabel sx={{ ml: 0.5 }}
          control={<Checkbox size="small" checked={eletronica} onChange={e => mudarEletronica(e.target.checked)} />}
          label="Assinatura eletrônica" />
      </Box>

      {erro && <Alert severity="error" sx={{ mx: 3, mt: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

      {carregando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!carregando && q && (
        <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%', px: { xs: 2, md: 4 }, py: 3 }}>

          {/* Barra das marcas de copiado (fora da impressão) */}
          <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
              Clique em cada número depois de copiar para o papel · <b>{nCopiados} de {nCampos}</b> copiados
            </Typography>
            <Button size="small" variant="text" onClick={desmarcarTudo} disabled={nCopiados === 0}>Desmarcar tudo</Button>
          </Box>

          {/* ══ A FOLHA (o que vai para o papel) ══ */}
          <Box ref={printRef} className="qs-folha" sx={{
            bgcolor: 'background.paper', color: 'text.primary',
            border: '1px solid', borderColor: 'divider', borderRadius: 1, p: { xs: 2, md: 3 },
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
              {!semLogo ? (
                <Box component="img" className="qs-logo" src={URL_LOGO}
                  alt="Secretaria Municipal de Saúde · Prefeitura de Milagres" onError={() => setSemLogo(true)}
                  sx={{ height: 64, width: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block' }} />
              ) : (
                <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }}>Prefeitura Municipal de Milagres — BA · Secretaria Municipal de Saúde</Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, textAlign: 'center', letterSpacing: '0.04em', mb: 1.5 }}>
              QUADRO DE ACOMPANHAMENTO DO SUPERVISOR
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'baseline', mb: 1 }}>
              <Typography sx={{ fontSize: '1rem' }}>Mês: <b>{nomeDoMes(q.periodo.inicio, q.periodo.fim)}</b></Typography>
              {nomeArea && <Typography sx={{ fontSize: '1rem' }}>Área: <b>{nomeArea}</b></Typography>}
              {carregado?.se && <Typography sx={{ fontSize: '1rem' }}>SE: <b>{carregado.se}</b></Typography>}
            </Box>
            <Box sx={{ mb: 1, pb: 1, borderBottom: '2px solid', borderColor: 'text.primary' }}>
              {ciclosFolha.length === 0 && (
                <Typography sx={{ fontSize: '1rem' }}>
                  Ciclo: <b>—</b>   Início: <b>{fmtDataBR(q.periodo.inicio)}</b>   Fim: <b>{fmtDataBR(q.periodo.fim)}</b>
                </Typography>
              )}
              {ciclosFolha.map(c => (
                <Typography key={c.nome} sx={{ fontSize: '1rem', display: 'flex', flexWrap: 'wrap', columnGap: 3 }}>
                  <span>Ciclo: <b>{c.nome}</b></span>
                  <span>Início: <b>{c.inicio}</b></span>
                  <span>Fim: <b>{c.fim}</b>{c.emAndamento ? ' (em andamento)' : ''}</span>
                </Typography>
              ))}
            </Box>

            {folha.map(sec => (
              <Box key={sec.titulo} className="qs-secao" sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, letterSpacing: '0.08em', borderBottom: '1px solid', borderColor: 'text.primary', pb: 0.3, mb: 0.5 }}>
                  {sec.titulo}
                </Typography>
                {sec.linhas.map((linha, i) => (
                  <Box key={i} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {linha.map(c => (
                      <CampoPapel key={c.id} campo={c} copiado={copiados[c.id] === c.valor} onClick={() => alternar(c)} />
                    ))}
                  </Box>
                ))}
              </Box>
            ))}

            {/* Assinatura (mesmo estilo da Comunicação e do Planner Férias) */}
            <Box className="qs-assin" sx={{ width: 340, maxWidth: '100%', mx: 'auto', mt: 5, textAlign: 'center' }}>
              <Box sx={{ minHeight: 34, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                {eletronica && (
                  <Box component="span" className="qs-assin-nome" sx={{
                    fontFamily: "'Kristi', cursive", fontStyle: 'normal', fontSize: 30, lineHeight: 1, color: AZUL_CANETA,
                    display: 'inline-block', transform: 'rotate(-3deg)', mb: '-4px', whiteSpace: 'nowrap',
                  }}>
                    {assinatura}
                  </Box>
                )}
              </Box>
              <Box sx={{ borderTop: '1px solid', borderColor: 'text.primary' }} />
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, pt: 0.5 }}>{assinatura}</Typography>
              <Typography sx={{ fontSize: '0.8rem' }}>{CARGO_ASSINATURA}</Typography>
            </Box>
          </Box>

          {/* ══ FAIXA "NÃO VAI PARA O PAPEL" (fora da impressão e do arquivo) ══ */}
          <Box className="no-print" sx={{
            mt: 4, p: 2, borderRadius: 1, border: '2px dashed', borderColor: 'divider', bgcolor: 'action.hover',
          }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1.5 }}>
              Não vai para o papel
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <GrupoFaixa titulo="IIP · IB">
                {([['Sede', q.sede], ['Zona Rural', q.zona_rural], ['P.E.', q.pe], ['Total geral', q.total_geral]] as [string, Bloco][]).map(([n, b]) => (
                  <ItemFaixa key={n} rotulo={n} valor={`${fmtIndice(b.iip)} · ${fmtIndice(b.ib)}`} vermelho />
                ))}
              </GrupoFaixa>
              <GrupoFaixa titulo="LIRAa (só informativo)">
                <ItemFaixa rotulo="Tubitos coletados no mês" valor={fmtNum(q.liraa.tubitos_coletados)} />
                <Typography sx={{ fontSize: '0.8rem', py: 0.3 }}>Amostras colet. da Sede = {contaAmostras(q.sede)}</Typography>
                {(q.zona_rural.amostras_coletadas_liraa ?? 0) > 0 && (
                  <Typography sx={{ fontSize: '0.8rem', py: 0.3 }}>Amostras colet. da Zona Rural = {contaAmostras(q.zona_rural)}</Typography>
                )}
                <ItemFaixa rotulo="Ciclo" valor={q.liraa.ciclos.length ? q.liraa.ciclos.join(', ') : '—'} />
                <ItemFaixa rotulo="Tubitos examinados" valor={fmtNum(q.liraa.tubitos_examinados)} />
                <ItemFaixa rotulo="Imóveis positivos aegypti" valor={fmtNum(q.liraa.imoveis_positivos_aegypti)} vermelho />
                <ItemFaixa rotulo="Imóveis positivos albopictus" valor={fmtNum(q.liraa.imoveis_positivos_albopictus)} vermelho />
                <ItemFaixa rotulo="Recipientes positivos aegypti" valor={fmtNum(q.liraa.recipientes_positivos_aegypti)} vermelho />
                <ItemFaixa rotulo="Recipientes positivos albopictus" valor={fmtNum(q.liraa.recipientes_positivos_albopictus)} vermelho />
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>Os tubitos entram em Amostras colet.; os positivos do LIRAa não entram na rotina.</Typography>
              </GrupoFaixa>
              <GrupoFaixa titulo="Total geral (Sede + Zona Rural + P.E.)">
                <ItemFaixa rotulo="Imóveis informados" valor={fmtNum(q.total_geral.imoveis_informados)} />
                <ItemFaixa rotulo="Imóveis trabalhados" valor={fmtNum(q.total_geral.imoveis_trabalhados)} />
                <ItemFaixa rotulo="Imóveis inspecionados" valor={fmtNum(q.total_geral.inspecionados)} />
                <ItemFaixa rotulo="Larvicida" valor={fmtGramas(q.total_geral.larvicida_gramas)} />
                <ItemFaixa rotulo="Depósitos tratados" valor={fmtNum(q.total_geral.depositos_tratados)} />
                <ItemFaixa rotulo="Agentes" valor={fmtNum(q.total_geral.agentes)} />
                <ItemFaixa rotulo="Dias trabalhados" valor={fmtNum(q.total_geral.dias_trabalhados)} />
              </GrupoFaixa>
            </Box>
          </Box>
        </Box>
      )}

      {!carregando && !q && !erro && (
        <Box sx={{ textAlign: 'center', mt: 8, color: 'text.disabled' }}>
          <Grading sx={{ fontSize: 64, mb: 2 }} />
          <Typography>Selecione o mês e clique em Filtrar.</Typography>
        </Box>
      )}

      {/* ── Impressão: só a folha, A4 retrato, preto; positivos em vermelho; sem marcas ── */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 15mm; }
          body { background: white !important; }
          .no-print, .filtros-bar { display: none !important; }
          ${cssPapel('.qs-folha')}
        }
        ${cssPapel('.qs-folha.qs-pdf')}
      `}</style>
    </Box>
  );
}
